<script setup>
import isMatch from 'lodash/isMatch'
import { useRoute } from 'vue-router'

defineProps({
    // enables using navigation wildcard to set tab
    addressNavigation: {
        type: Boolean,
        default: false,
    },
    tabs: {
        type: Array,
        default: () => [],
    },
    activeTab: {
        type: Object,
        default: null,
    },
    isUnderlined: {
        type: Boolean,
        default: false,
    },
    isUnpadded: {
        type: Boolean,
        default: false,
    },
})

const emit = defineEmits(['setActive'])

const route = useRoute()

function isActive(tabRoute, { name, params, path }) {
    if (typeof tabRoute === 'string') return path === tabRoute

    return isMatch(tabRoute, Object.keys(params).length > 0 ? { name, params } : { name })
}
</script>

<template>
    <BaseContent>
        <div class="tab-panel">
            <BaseSection padded>
                <div class="row">
                    <div
                        v-if="addressNavigation"
                        :class="['tab-list', isUnpadded ? 'tab-list-unpadded' : '']"
                    >
                        <BaseLink
                            v-for="tab in tabs"
                            :key="tab.label"
                            :class="isActive(tab.to, route) ? 'tab active' : 'tab'"
                            :to="tab.to"
                        >
                            {{ tab.label }}
                        </BaseLink>
                    </div>
                    <div
                        v-else
                        :class="['tab-list', isUnpadded ? 'tab-list-unpadded' : '']"
                    >
                        <div
                            v-for="tab in tabs"
                            :key="tab.label"
                            :class="activeTab === tab ? 'tab active' : 'tab'"
                            @click="emit('setActive', tab)"
                        >
                            {{ tab.label }}
                        </div>
                    </div>
                    <div class="side-slot">
                        <slot />
                    </div>
                </div>
                <div
                    v-if="isUnderlined"
                    class="separator-line"
                />
            </BaseSection>
        </div>
    </BaseContent>
</template>

<style lang="scss" scoped>
@use 'sass:math';
@use '@design' as *;

.row {
    display: flex;
    flex-direction: row;
    justify-content: center;
    padding-top: $s-xl;

    @include desktop {
        justify-content: space-between;
    }
}

.tab-panel {
    margin-top: $s-m;

    @include desktop {
        margin-top: $s-xl;
    }
}
.side-slot {
    display: flex;
    margin-left: auto;
}

.tab {
    @extend %caption;

    position: relative;
    padding: 0 0 $s-m;
    margin: 0;
    color: $c-text-secondary;
    cursor: pointer;

    &:hover,
    &:active {
        text-decoration: none;
    }

    &::after {
        position: absolute;
        bottom: 0;
        left: 0;
        display: block;
        width: 100%;
        height: 4px;
        content: '';
        background: $c-highlight;
        transform: scaleX(0);

        @include transition(transform);
    }

    &.active {
        color: $c-text-primary;

        &::after {
            transform: scaleX(1);
        }
    }
}

.tab ~ .tab {
    margin-left: $s-xs;
}

.tab-list {
    @include scrollableWrapper;

    display: flex;
    justify-content: center;
    width: 100%;
    padding-bottom: math.div($s-h, 2);

    &-unpadded {
        padding-bottom: 0;

        .tab {
            margin-bottom: 0;
        }
    }

    @include tablet {
        justify-content: flex-start;
        width: auto;
    }

    @include desktop {
        justify-content: flex-start;
    }
}

.separator-line {
    width: 100%;
    height: 1px;
    opacity: 0.1;

    &::after {
        display: block;
        width: 100%;
        height: 100%;
        content: '';
        background: $c-text-tertiary;
    }
}

@include desktop {
    .tab {
        padding: 0 $s-m $s-m;
    }

    .tab ~ .tab {
        margin-left: $s-m;
    }
}
</style>
